import { media } from './site';

export type ServiceLink = { label: string; href?: string; description?: string };
export type ServiceCta = { label: string; href: string };
export type ServiceCluster = { title: string; items: ServiceLink[]; detail?: string };
export type ServiceGroup = {
  id: string;
  title: string;
  summary: string;
  clusters: ServiceCluster[];
  pageHref?: string;
  ctas?: ServiceCta[];
  image?: string;
  alt?: string;
  concept?: boolean;
};

// Homepage/general service links lead to each group's MAIN page (no in-page fragments).
export const featuredServices: ServiceGroup[] = [
  {
    id: 'residential',
    title: 'Residential landscapes & outdoor living',
    summary: 'Create a place to gather, unwind and enjoy the outdoors, with planting, stonework and outdoor features planned together.',
    clusters: [
      {
        title: 'Landscape foundations',
        items: [
          { label: 'Landscape design & installation', href: '/residential-landscapes' },
          { label: 'Landscape renovation', href: '/residential-landscapes' },
          { label: 'Brick pavers & hardscape', href: '/residential-landscapes' },
        ],
        detail: 'Patios, walkways, retaining walls and natural stone.',
      },
      {
        title: 'Garden features',
        items: [
          { label: 'Outdoor kitchens & living areas', href: '/residential-landscapes' },
          { label: 'Fireplaces & fire pits', href: '/residential-landscapes' },
          { label: 'Outdoor lighting', href: '/residential-landscapes' },
        ],
      },
    ],
    pageHref: '/residential-landscapes',
    ctas: [{ label: 'Explore residential landscapes', href: '/residential-landscapes' }],
    image: media.landscapeHero,
    alt: 'Illustrative residential landscape with a limestone walk, layered planting and a landscaped home',
    concept: true,
  },
  {
    id: 'land-development',
    title: 'Land development',
    summary: 'Establish new sites and communities with landscape planting, ground cover and stabilization at the scale your property needs.',
    clusters: [
      {
        title: 'Plant & establish',
        items: [
          { label: 'Site plantings & street trees', href: '/land-development' },
          { label: 'Hydroseeding, seeding & sod', href: '/land-development' },
          { label: 'Entry features & retaining walls', href: '/land-development' },
        ],
      },
      {
        title: 'Stabilize & protect',
        items: [
          { label: 'Soil, pond & swale stabilization', href: '/land-development' },
          { label: 'Erosion matting & straw blowing', href: '/land-development' },
        ],
      },
    ],
    pageHref: '/land-development',
    ctas: [{ label: 'Explore land development', href: '/land-development' }],
    image: media.landDevelopment,
    alt: 'D.A. Alexander crew transporting mature trees for site planting',
    concept: false,
  },
];

export const directoryServices: ServiceGroup[] = [
  {
    id: 'property-care',
    title: 'Commercial landscape care',
    image: 'https://codex.websiteforge.net/da-alexander-redesign-20261028/assets/service-care-commercial-20261103.png',
    alt: 'Maintained lawn, planting beds and shade trees outside a brick-and-glass office building.',
    concept: true,
    summary: 'Maintain commercial properties and HOA grounds with regular landscape upkeep and seasonal lawn care.',
    clusters: [
      {
        title: 'Property upkeep',
        items: [
          { label: 'Landscape maintenance', href: '/commercial-landscape-care' },
          { label: 'Mowing, bed care & pruning', href: '/commercial-landscape-care' },
          { label: 'Spring & fall cleanup', href: '/commercial-landscape-care' },
        ],
      },
      {
        title: 'Lawn programs',
        items: [
          { label: 'Fertilization & weed control', href: '/commercial-landscape-care' },
          { label: 'Grub prevention', href: '/commercial-landscape-care' },
          { label: 'Core aeration', href: '/commercial-landscape-care' },
        ],
      },
    ],
    pageHref: '/commercial-landscape-care',
    ctas: [{ label: 'Explore commercial landscape care', href: '/commercial-landscape-care' }],
  },
  {
    id: 'commercial',
    title: 'Commercial & seasonal services',
    image: 'https://codex.websiteforge.net/da-alexander-redesign-20261028/assets/service-commercial-20261029.png',
    alt: 'Illustrative concept image of a snowplow clearing a commercial property parking lot',
    concept: true,
    summary: 'Keep commercial properties, HOA communities and active sites cared for, from routine cleanup to snow and ice management.',
    clusters: [
      {
        title: 'Winter care',
        items: [
          { label: 'Commercial snow removal & salting', href: '/commercial-seasonal-services', description: 'Snow and ice management.' },
        ],
      },
      {
        title: 'Grounds & sites',
        items: [
          { label: 'Parking lot & road services', href: '/commercial-seasonal-services', description: 'Sweeping, pavement maintenance and repairs.' },
          { label: 'Construction site services', href: '/commercial-seasonal-services', description: 'Site cleanup.' },
        ],
      },
    ],
    pageHref: '/commercial-seasonal-services',
    ctas: [{ label: 'Explore commercial & seasonal services', href: '/commercial-seasonal-services' }],
  },
];

export const allServiceGroups: ServiceGroup[] = [...featuredServices, ...directoryServices];
